import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableAction, TableColumn, TableComponent } from '../../common/table/table.component';
import { TenantsService } from '../../services/tenants.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantServiceDbService } from '../../services/tenant-service-db.service';
import { TenantServiceAssignment } from '../../models/tenant-service.model';
import { DbConfigResponse, DbType, MdmDbCredentials } from '../../models/tenant-service-db.model';
import { TenantServiceModalMode, TenantServicesModalComponent } from '../../modals/tenant-services/tenant-services-modal.component';
import { TenantServiceDbComponent } from '../tenant-service-db/tenant-service-db.component';
import { TenantServiceMdmDbConfigComponent } from '../../modals/tenant-service-mdm-db-config/tenant-service-mdm-db-config.component';
import { LoaderComponent } from '../../common/loader/loader.component';

const DB_CONFIGURABLE_SERVICE = 'datastream_gui';
const MDM_CONFIGURABLE_SERVICE = 'mdm';

const DB_TYPE_LABELS: Record<DbType, string> = {
  MSSQL: 'MSSQL/Synapse',
  SNOWFLAKE: 'Snowflake',
  DATABRICKS: 'Databricks'
};

@Component({
  selector: 'app-tenant-services',
  imports: [TableComponent, TenantServicesModalComponent, TenantServiceDbComponent, TenantServiceMdmDbConfigComponent, LoaderComponent],
  templateUrl: './tenant-services.component.html',
  styleUrl: './tenant-services.component.scss'
})
export class TenantServicesComponent implements OnInit {
  tenantUid: string;

  actions: TableAction[] = ['edit', 'delete'];

  columns: TableColumn[] = [
    { key: 'service_name', label: 'Service Name' },
    { key: 'is_active', label: 'Active', type: 'boolean' },
    {
      key: 'dbConfig',
      label: 'DB Configuration',
      type: 'button',
      buttonIcon: 'bi-database-gear',
      buttonLabel: 'Configure',
      buttonVisibleKey: 'dbConfigVisible',
      buttonLabelKey: 'dbConfigLabel',
      emptyPlaceholder: 'Not applicable'
    }
  ];

  services: TenantServiceAssignment[] = [];
  rows: any[] = [];
  loading = true;

  modalOpen = false;
  modalMode: TenantServiceModalMode = 'add';
  selectedService: TenantServiceAssignment | null = null;

  dbModalOpen = false;
  dbConfigured = false;
  dbActiveType: DbType | null = null;
  dbConfigResponse?: DbConfigResponse | null;

  mdmModalOpen = false;
  mdmConfigured = false;
  mdmConfigResponse?: MdmDbCredentials | null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tenantsService: TenantsService,
    private toastrService: ToastrService,
    private tenantServiceDbService: TenantServiceDbService
  ) {
    this.tenantUid = this.route.snapshot.paramMap.get('slug') ?? '';
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.tenantsService.getTenantServices(this.tenantUid).subscribe({
      next: (services) => {
        this.services = services;
        this.loading = false;
        this.buildRows();
        this.loadDbConfigStatus();
        this.loadMdmConfigStatus();
      },
      error: () => {
        this.loading = false;
        this.toastrService.error('Unable to load services.');
      }
    });
  }

  private loadDbConfigStatus(): void {
    this.tenantServiceDbService.getDbConfig(this.tenantUid).subscribe({
      next: (res) => {
        this.dbConfigResponse = res;
        this.dbConfigured = !!res.config;
        this.dbActiveType = res.active_db_type;
        this.buildRows();
      },
      error: () => {
        this.dbConfigResponse = null;
        this.dbConfigured = false;
        this.dbActiveType = null;
        this.buildRows();
      }
    });
  }

  private loadMdmConfigStatus(): void {
    this.tenantServiceDbService.getMdmDbConfig(this.tenantUid).subscribe({
      next: (res) => {
        this.mdmConfigResponse = res;
        this.mdmConfigured = true;
        this.buildRows();
      },
      error: () => {
        this.mdmConfigResponse = null;
        this.mdmConfigured = false;
        this.buildRows();
      }
    });
  }

  private buildRows(): void {
    this.rows = this.services.map(service => {
      const serviceKey = service.service_name?.toLowerCase();
      const isDatastream = serviceKey === DB_CONFIGURABLE_SERVICE;
      const isMdm = serviceKey === MDM_CONFIGURABLE_SERVICE;

      let dbConfigLabel = 'Configure';
      if (isDatastream) {
        dbConfigLabel = this.dbConfigured ? `Configured · ${DB_TYPE_LABELS[this.dbActiveType!]}` : 'Configure';
      } else if (isMdm) {
        dbConfigLabel = this.mdmConfigured ? 'Configured' : 'Configure';
      }

      return {
        ...service,
        dbConfigVisible: isDatastream || isMdm,
        dbConfigLabel
      };
    });
  }

  goBack(): void {
    this.router.navigate(['/tenants', this.tenantUid]);
  }

  openAdd(): void {
    this.selectedService = null;
    this.modalMode = 'add';
    this.modalOpen = true;
  }

  onEdit(row: TenantServiceAssignment): void {
    this.selectedService = row;
    this.modalMode = 'edit';
    this.modalOpen = true;
  }

  onDelete(row: TenantServiceAssignment): void {
    this.selectedService = row;
    this.modalMode = 'delete';
    this.modalOpen = true;
  }

  onModalClosed(): void {
    this.modalOpen = false;
    this.selectedService = null;
  }

  onModalSaved(): void {
    const messages: Record<TenantServiceModalMode, string> = {
      add: 'Service added successfully.',
      edit: 'Service updated successfully.',
      delete: 'Service removed successfully.'
    };
    this.toastrService.success(messages[this.modalMode]);
    this.load();
  }

  onColumnAction(event: { key: string; row: any }): void {
    if (event.key !== 'dbConfig') {
      return;
    }

    const serviceKey = (event.row.service_name as string | undefined)?.toLowerCase();
    if (serviceKey === MDM_CONFIGURABLE_SERVICE) {
      this.mdmModalOpen = true;
    } else {
      this.dbModalOpen = true;
    }
  }

  onDbModalClosed(): void {
    this.dbModalOpen = false;
  }

  onDbModalSaved(): void {
    this.toastrService.success('Database configuration saved successfully.');
    this.loadDbConfigStatus();
  }

  onMdmModalClosed(): void {
    this.mdmModalOpen = false;
  }

  onMdmModalSaved(): void {
    this.toastrService.success('MDM database configuration saved successfully.');
    this.loadMdmConfigStatus();
  }
}
